import React, { useRef, useState, useEffect } from 'react';
import { useUiStore } from '@/store/uiStore';
import { useProjectStore } from '@/store/projectStore';
import { useSimulationStore } from '@/store/simulationStore';
import { useHMIEditorStore } from './store/hmiEditorStore';
import { HMIComponentRenderer } from './HMIComponentRenderer';
import { isTextInputTarget } from '@/app/shortcuts/shortcuts';
import styles from './HMIEditor.module.css';

export function HMIEditor() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { project } = useProjectStore();
  const { hmi } = project;
  const { 
    activeScreenId, setActiveScreen, 
    zoom, panX, panY, setZoom, setPan,
    selectedComponentIds, setSelectedComponents,
    updateComponent 
  } = useHMIEditorStore();

  const activeMode = useUiStore((s) => s.activeMode);
  const simState = useSimulationStore((s) => s.snapshot.state);
  const isSimRunning = simState === 'RUNNING' || simState === 'PAUSED';
  const isDesigner = activeMode === 'hmi' && !isSimRunning;
  
  // Set initial active screen if none selected or if current selection is invalid
  useEffect(() => {
    const screenExists = hmi.screens.some(s => s.id === activeScreenId);
    if ((!activeScreenId || !screenExists) && hmi.screens.length > 0) {
      const firstId = hmi.screens[0]?.id;
      if (firstId) setActiveScreen(hmi.defaultScreenId || firstId);
    }
  }, [activeScreenId, hmi, setActiveScreen]);

  const activeScreen = hmi.screens.find(s => s.id === activeScreenId);

  // Pan logic
  const [isPanning, setIsPanning] = useState(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const isSpacePressed = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTextInputTarget(e.target) || (document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName))) {
        return;
      }
      if (e.code === 'Space') {
        isSpacePressed.current = true;
        if (containerRef.current) containerRef.current.style.cursor = 'grab';
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        isSpacePressed.current = false;
        if (containerRef.current) containerRef.current.style.cursor = 'default';
        setIsPanning(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button === 1 || isSpacePressed.current) {
      setIsPanning(true);
      lastMouse.current = { x: e.clientX, y: e.clientY };
      e.currentTarget.setPointerCapture(e.pointerId);
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
      return;
    }
    
    // Clear selection if clicking on background in designer mode
    if (isDesigner && (e.target === e.currentTarget || (e.target as Element).className.includes('canvas'))) {
      setSelectedComponents([]);
      useUiStore.getState().clearSelection();
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isPanning) {
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      setPan(panX + dx, panY + dy);
      lastMouse.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isPanning) {
      setIsPanning(false);
      e.currentTarget.releasePointerCapture(e.pointerId);
      if (containerRef.current) containerRef.current.style.cursor = isSpacePressed.current ? 'grab' : 'default';
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const zoomFactor = 1.1;
      const newZoom = e.deltaY < 0 ? zoom * zoomFactor : zoom / zoomFactor;
      setZoom(Math.max(0.1, Math.min(newZoom, 5)));
    } else {
      setPan(panX - e.deltaX, panY - e.deltaY);
    }
  };

  if (!activeScreen) {
    return (
      <div className={styles.empty} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-text-muted)' }}>
        <p style={{ marginBottom: 16 }}>No HMI Screen available. Create one to start.</p>
        <button 
          onClick={() => {
            const newScreenId = `screen_${Date.now()}`;
            useProjectStore.getState().setHmiProject({
              ...hmi,
              screens: [...hmi.screens, { id: newScreenId, name: `Screen ${hmi.screens.length + 1}`, width: 800, height: 600, backgroundColor: '#1e1e1e', components: [] }],
              defaultScreenId: hmi.defaultScreenId || newScreenId
            });
            setActiveScreen(newScreenId);
          }}
          style={{ padding: '8px 16px', background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold' }}
        >
          Create Screen
        </button>
      </div>
    );
  }

  return (
    <div 
      className={styles.container}
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onWheel={handleWheel}
    >
      {isSimRunning && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(16, 185, 129, 0.25)',
            color: '#10b981',
            border: '1px solid #10b981',
            padding: '5px 16px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            zIndex: 100,
            backdropFilter: 'blur(4px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            pointerEvents: 'none',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981' }} />
          CANLI SİMÜLASYON MODU (Operatör Paneli Aktif)
        </div>
      )}

      <div 
        className={styles.canvas}
        style={{
          width: activeScreen.width,
          height: activeScreen.height,
          backgroundColor: activeScreen.backgroundColor,
          transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
          transformOrigin: '0 0'
        }}
      >
        {activeScreen.components.map(comp => (
          <div 
            key={comp.id}
            style={{
              position: 'absolute',
              left: comp.x,
              top: comp.y,
              width: comp.width,
              height: comp.height,
              border: isDesigner && selectedComponentIds.includes(comp.id) ? '2px solid var(--color-primary)' : 'none',
              cursor: isDesigner ? 'pointer' : 'default'
            }}
            onPointerDown={(e) => {
              if (isDesigner && e.button === 0 && !isSpacePressed.current) {
                e.stopPropagation();
                setSelectedComponents([comp.id]);
                useUiStore.getState().select(comp.id, 'hmiComponent');
                
                // Start drag
                e.currentTarget.setPointerCapture(e.pointerId);
                const startX = e.clientX;
                const startY = e.clientY;
                const origX = comp.x;
                const origY = comp.y;
                
                const onMove = (me: PointerEvent) => {
                  const dx = (me.clientX - startX) / zoom;
                  const dy = (me.clientY - startY) / zoom;
                  updateComponent(comp.id, {
                    x: Math.round(origX + dx),
                    y: Math.round(origY + dy)
                  });
                };
                
                const onUp = (ue: PointerEvent) => {
                  const target = ue.currentTarget as HTMLElement;
                  target.removeEventListener('pointermove', onMove);
                  target.removeEventListener('pointerup', onUp);
                  target.releasePointerCapture(ue.pointerId);
                };
                
                e.currentTarget.addEventListener('pointermove', onMove);
                e.currentTarget.addEventListener('pointerup', onUp);
              }
            }}
          >
            <HMIComponentRenderer component={comp} isRuntime={isSimRunning} />
          </div>
        ))}
      </div>
    </div>
  );
}
